import { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { rolesApi, notificationsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import type { Role } from '@/types';

type NotifLevel = 'info' | 'warning' | 'critical';
type RolePref = { enabled: boolean; minLevel: NotifLevel };

const permissionGroups = [
  {
    name: 'Organizaciones',
    permissions: [
      { key: PERMISSIONS.ORG_READ, label: 'Ver' },
      { key: PERMISSIONS.ORG_CREATE, label: 'Crear' },
      { key: PERMISSIONS.ORG_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.ORG_DELETE, label: 'Eliminar' },
    ],
  },
  {
    name: 'Usuarios',
    permissions: [
      { key: PERMISSIONS.USERS_READ, label: 'Ver' },
      { key: PERMISSIONS.USERS_CREATE, label: 'Crear' },
      { key: PERMISSIONS.USERS_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.USERS_DELETE, label: 'Eliminar' },
      { key: PERMISSIONS.ROLES_MANAGE, label: 'Gestionar roles de la organización' },
    ],
  },
  {
    name: 'CMS (Page Builder)',
    permissions: [
      { key: PERMISSIONS.CMS_READ, label: 'Ver' },
      { key: PERMISSIONS.CMS_CREATE, label: 'Crear' },
      { key: PERMISSIONS.CMS_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.CMS_PUBLISH, label: 'Publicar' },
      { key: PERMISSIONS.CMS_DELETE, label: 'Eliminar' },
    ],
  },
  {
    name: 'Contenido (compatibilidad)',
    permissions: [
      { key: PERMISSIONS.CONTENT_READ, label: 'Ver' },
      { key: PERMISSIONS.CONTENT_CREATE, label: 'Crear' },
      { key: PERMISSIONS.CONTENT_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.CONTENT_DELETE, label: 'Eliminar' },
    ],
  },
  {
    name: 'Media',
    permissions: [
      { key: PERMISSIONS.MEDIA_READ, label: 'Ver' },
      { key: PERMISSIONS.MEDIA_CREATE, label: 'Subir' },
      { key: PERMISSIONS.MEDIA_DELETE, label: 'Eliminar' },
    ],
  },
  {
    name: 'Base de datos',
    permissions: [
      { key: PERMISSIONS.DB_READ, label: 'Ver' },
      { key: PERMISSIONS.DB_BACKUP, label: 'Backup' },
      { key: PERMISSIONS.DB_RESTORE, label: 'Restaurar' },
      { key: PERMISSIONS.DB_CREATE, label: 'Crear' },
      { key: PERMISSIONS.DB_DELETE, label: 'Eliminar' },
    ],
  },
  {
    name: 'Configuración',
    permissions: [
      { key: PERMISSIONS.SETTINGS_READ, label: 'Ver' },
      { key: PERMISSIONS.SETTINGS_UPDATE, label: 'Editar' },
    ],
  },
  {
    name: 'Auditoría',
    permissions: [{ key: PERMISSIONS.AUDIT_READ, label: 'Ver logs' }],
  },
  {
    name: 'Sistema',
    permissions: [{ key: PERMISSIONS.SYSTEM_READ, label: 'Ver' }],
  },
  {
    name: 'Ecommerce',
    permissions: [
      { key: PERMISSIONS.ECOMMERCE_READ, label: 'Ver' },
      { key: PERMISSIONS.ECOMMERCE_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.ORDERS_READ, label: 'Ver pedidos' },
      { key: PERMISSIONS.ORDERS_UPDATE, label: 'Gestionar pedidos' },
    ],
  },
  {
    name: 'Catálogo',
    permissions: [
      { key: PERMISSIONS.CATALOG_READ, label: 'Ver' },
      { key: PERMISSIONS.CATALOG_UPDATE, label: 'Editar' },
    ],
  },
  {
    name: 'Productos',
    permissions: [
      { key: PERMISSIONS.PRODUCTS_READ, label: 'Ver' },
      { key: PERMISSIONS.PRODUCTS_CREATE, label: 'Crear' },
      { key: PERMISSIONS.PRODUCTS_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.PRODUCTS_DELETE, label: 'Eliminar' },
      { key: PERMISSIONS.PRODUCTS_PUBLISH, label: 'Publicar' },
      { key: PERMISSIONS.PRODUCTS_TAG, label: 'Etiquetar' },
    ],
  },
  {
    name: 'Proyectos',
    permissions: [
      { key: PERMISSIONS.PROJECTS_READ, label: 'Ver' },
      { key: PERMISSIONS.PROJECTS_CREATE, label: 'Crear' },
      { key: PERMISSIONS.PROJECTS_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.PROJECTS_DELETE, label: 'Eliminar' },
      { key: PERMISSIONS.PROJECTS_ASSIGN, label: 'Asignar usuarios' },
    ],
  },
  {
    name: 'Metadatos',
    permissions: [
      { key: PERMISSIONS.METADATA_READ, label: 'Ver' },
      { key: PERMISSIONS.METADATA_CREATE, label: 'Crear' },
      { key: PERMISSIONS.METADATA_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.METADATA_DELETE, label: 'Eliminar' },
      { key: PERMISSIONS.METADATA_ADMIN, label: 'Admin' },
      { key: PERMISSIONS.METADATA_DELEGATE, label: 'Delegar' },
    ],
  },
  {
    name: 'Blog',
    permissions: [
      { key: PERMISSIONS.BLOG_READ, label: 'Ver' },
      { key: PERMISSIONS.BLOG_CREATE, label: 'Crear' },
      { key: PERMISSIONS.BLOG_UPDATE, label: 'Editar' },
      { key: PERMISSIONS.BLOG_PUBLISH, label: 'Publicar' },
      { key: PERMISSIONS.BLOG_DELETE, label: 'Eliminar' },
    ],
  },
  {
    // Grupo propio: se puede dar a quien atienda las altas de colaboradores sin
    // convertirlo en administrador de la organizacion.
    name: 'Colaboradores (tejedores)',
    permissions: [
      { key: PERMISSIONS.WEAVERS_READ, label: 'Ver solicitudes' },
      { key: PERMISSIONS.WEAVERS_MANAGE, label: 'Aprobar y dar de alta' },
      { key: PERMISSIONS.COLLABORATORS_READ, label: 'Ver fichas de colaboradores' },
      { key: PERMISSIONS.COLLABORATORS_MANAGE, label: 'Editar fichas de colaboradores' },
    ],
  },
  {
    name: 'CRM',
    permissions: [
      { key: PERMISSIONS.CRM_READ, label: 'Ver' },
      { key: PERMISSIONS.CRM_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Reuniones IA',
    permissions: [
      { key: PERMISSIONS.MEETINGS_READ, label: 'Ver' },
      { key: PERMISSIONS.MEETINGS_MANAGE, label: 'Gestionar' },
      { key: PERMISSIONS.MEETINGS_SHARE, label: 'Compartir' },
    ],
  },
  {
    name: 'Marketing',
    permissions: [
      { key: PERMISSIONS.MARKETING_READ, label: 'Ver' },
      { key: PERMISSIONS.MARKETING_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Planificador',
    permissions: [
      { key: PERMISSIONS.PLANNER_READ, label: 'Ver' },
      { key: PERMISSIONS.PLANNER_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Procesos',
    permissions: [
      { key: PERMISSIONS.PROCESSES_READ, label: 'Ver' },
      { key: PERMISSIONS.PROCESSES_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Presencia',
    permissions: [
      { key: PERMISSIONS.PRESENCE_READ, label: 'Ver' },
      { key: PERMISSIONS.PRESENCE_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Asociados',
    permissions: [
      { key: PERMISSIONS.MEMBERS_READ, label: 'Ver' },
      { key: PERMISSIONS.MEMBERS_MANAGE, label: 'Gestionar' },
    ],
  },
  {
    name: 'Cuotas',
    permissions: [
      { key: PERMISSIONS.FEES_READ, label: 'Ver' },
      { key: PERMISSIONS.FEES_MANAGE, label: 'Gestionar' },
      { key: PERMISSIONS.FEES_SEND, label: 'Enviar' },
    ],
  },
  {
    name: 'Presupuesto',
    permissions: [
      { key: PERMISSIONS.BUDGET_READ, label: 'Ver' },
      { key: PERMISSIONS.BUDGET_MANAGE, label: 'Gestionar' },
      { key: PERMISSIONS.BUDGET_APPROVE, label: 'Aprobar' },
    ],
  },
  {
    name: 'Otros',
    permissions: [
      { key: PERMISSIONS.ORG_INVITE, label: 'Invitar usuarios' },
      { key: PERMISSIONS.AI_USE, label: 'Usar IA' },
    ],
  },
];

export default function RolesPage() {
  const { hasPermission } = useAuthStore();
  const selectedOrg = useOrganizationStore((s) => s.selectedOrg);
  // VIDA-293 Fase B: un admin de org con roles:manage gestiona los roles de SU
  // organización. El super_admin además gestiona los globales.
  const isSuperAdmin = hasPermission('*');
  const canManageRoles = isSuperAdmin || hasPermission(PERMISSIONS.ROLES_MANAGE);
  const canManageNotif = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.ORG_UPDATE));
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  // Per-role email notification preferences for the selected org (roleId -> pref).
  const [rolePrefs, setRolePrefs] = useState<Record<string, RolePref>>({});
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });

  useEffect(() => {
    loadRoles();
  }, []);

  // Load per-role notification preferences for the selected org.
  useEffect(() => {
    if (!selectedOrg?._id || !canManageNotif) { setRolePrefs({}); return; }
    let cancelled = false;
    notificationsApi.getRolePreferences()
      .then((r) => { if (!cancelled) setRolePrefs((r.data?.data || {}) as Record<string, RolePref>); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedOrg?._id, canManageNotif]);

  const loadRoles = async () => {
    try {
      const response = await rolesApi.list();
      setRoles(response.data.roles || []);
    } catch (error) {
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const saveRolePref = async (roleId: string, next: RolePref) => {
    setRolePrefs((prev) => ({ ...prev, [roleId]: next })); // optimistic
    try {
      await notificationsApi.saveRolePreference(roleId, { email: next });
    } catch {
      toast.error('Error al guardar preferencia del rol');
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || formData.permissions.length === 0) {
      toast.error('Nombre y al menos un permiso son requeridos');
      return;
    }

    if (!isSuperAdmin && !selectedOrg) {
      toast.error('Selecciona una organización para crear el rol');
      return;
    }

    try {
      await rolesApi.create({
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions,
        // El super_admin crea roles globales; un admin de org los crea dentro de
        // su organización (el backend fuerza el ámbito de todos modos).
        orgId: isSuperAdmin ? undefined : selectedOrg?._id,
      });
      toast.success(isSuperAdmin ? 'Rol global creado' : `Rol creado en ${selectedOrg?.name}`);
      setShowCreateModal(false);
      setFormData({ name: '', description: '', permissions: [] });
      loadRoles();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear rol';
      toast.error(message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || editFormData.permissions.length === 0) {
      toast.error('Al menos un permiso es requerido');
      return;
    }

    try {
      await rolesApi.update(selectedRole._id, {
        name: editFormData.name,
        description: editFormData.description,
        permissions: editFormData.permissions,
      });
      toast.success('Rol actualizado');
      setShowEditModal(false);
      setSelectedRole(null);
      setEditFormData({ name: '', description: '', permissions: [] });
      loadRoles();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al actualizar rol';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    setDeleteLoading(true);
    try {
      await rolesApi.delete(selectedRole._id);
      toast.success('Rol eliminado');
      setShowDeleteConfirm(false);
      setSelectedRole(null);
      loadRoles();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const togglePermission = (permission: string, isEdit = false) => {
    const setFunc = isEdit ? setEditFormData : setFormData;
    setFunc((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleSeedDemo = async () => {
    try {
      const res = await rolesApi.seedDemo();
      toast.success(res.data.created ? 'Rol demo_admin creado' : 'Rol demo_admin actualizado');
      loadRoles();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear rol demo';
      toast.error(message);
    }
  };

  const handleSeedPresence = async () => {
    try {
      const res = await rolesApi.seedPresenceSupervisor();
      toast.success(res.data.created ? 'Rol Control de presencia creado' : 'Rol Control de presencia actualizado');
      loadRoles();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear rol';
      toast.error(message);
    }
  };

  const openEditModal = (role: Role) => {
    setSelectedRole(role);
    setEditFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions,
    });
    setShowEditModal(true);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Roles y Permisos"
        subtitle="Gestiona los roles y sus permisos"
        actions={
          canManageRoles && (
            <div className="flex gap-2">
              {/* Los seeds crean roles GLOBALES → solo super_admin. */}
              {hasPermission('*') && (
                <>
                  <button onClick={handleSeedDemo} className="btn-secondary">
                    Crear Rol Demo
                  </button>
                  <button onClick={handleSeedPresence} className="btn-secondary">
                    Crear Rol Control de presencia
                  </button>
                </>
              )}
              <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Nuevo rol
              </button>
            </div>
          )
        }
      />

      <div className="p-6">
        {roles.length === 0 ? (
          <EmptyState
            title="No hay roles personalizados"
            description="Los roles del sistema están predefinidos"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role) => (
              <div key={role._id} className="card">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          role.is_system ? 'bg-amber-100' : 'bg-vida-100'
                        }`}
                      >
                        {role.is_system ? (
                          <LockClosedIcon className="w-5 h-5 text-amber-600" />
                        ) : (
                          <ShieldCheckIcon className="w-5 h-5 text-vida-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{role.name}</h3>
                        {role.is_system && (
                          <span className="text-xs text-amber-600">Rol del sistema</span>
                        )}
                      </div>
                    </div>
                    {/* Editar/borrar: super_admin cualquiera; admin de org solo
                        los roles de SU organización (el backend lo revalida). */}
                    {!role.is_system &&
                      canManageRoles &&
                      (isSuperAdmin || (!!role.organization_id && role.organization_id === selectedOrg?._id)) && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(role)}
                          className="p-1 text-gray-400 hover:text-vida-600"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRole(role);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {role.description && (
                    <p className="mt-3 text-sm text-gray-500">{role.description}</p>
                  )}

                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                      Permisos ({role.permissions.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.includes('*') ? (
                        <span className="badge-success">Todos los permisos</span>
                      ) : (
                        role.permissions.slice(0, 5).map((perm) => (
                          <span key={perm} className="badge-info text-xs">
                            {perm}
                          </span>
                        ))
                      )}
                      {role.permissions.length > 5 && !role.permissions.includes('*') && (
                        <span className="badge-gray text-xs">
                          +{role.permissions.length - 5} más
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Per-role email notification preference (for the selected org) */}
                  {canManageNotif && selectedOrg?._id && (() => {
                    const pref = rolePrefs[role._id] || { enabled: true, minLevel: 'info' as NotifLevel };
                    return (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                          Notificaciones por email · {selectedOrg.name}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-600">Recibir emails</span>
                          <button
                            type="button"
                            onClick={() => saveRolePref(role._id, { ...pref, enabled: !pref.enabled })}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${pref.enabled ? 'bg-vida-600' : 'bg-gray-200'}`}
                            title="Aplica a los miembros con este rol que no tengan preferencia propia"
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${pref.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        {pref.enabled && (
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <span className="text-sm text-gray-600">A partir de nivel</span>
                            <select
                              value={pref.minLevel}
                              onChange={(e) => saveRolePref(role._id, { ...pref, minLevel: e.target.value as NotifLevel })}
                              className="rounded-lg border-gray-300 shadow-sm text-xs focus:ring-vida-500 focus:border-vida-500 py-1"
                            >
                              <option value="info">Info (todas)</option>
                              <option value="warning">Aviso</option>
                              <option value="critical">Crítico</option>
                            </select>
                          </div>
                        )}
                        <p className="text-[11px] text-gray-400 mt-2">La preferencia personal de cada usuario tiene prioridad.</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nuevo rol"
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="label">Nombre del rol *</label>
            <input
              type="text"
              className="input"
              placeholder="Moderador"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Descripción</label>
            <input
              type="text"
              className="input"
              placeholder="Descripción del rol"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div>
            <label className="label mb-3">Permisos *</label>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {permissionGroups.map((group) => (
                <div key={group.name} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">{group.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.permissions.map((perm) => (
                      <label
                        key={perm.key}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                          formData.permissions.includes(perm.key)
                            ? 'bg-vida-100 text-vida-700 border-vida-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } border`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={formData.permissions.includes(perm.key)}
                          onChange={() => togglePermission(perm.key)}
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Crear rol
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedRole(null); }}
        title="Editar rol"
        size="lg"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="label">Nombre del rol *</label>
            <input
              type="text"
              className="input"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Descripción</label>
            <input
              type="text"
              className="input"
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />
          </div>

          <div>
            <label className="label mb-3">Permisos *</label>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {permissionGroups.map((group) => (
                <div key={group.name} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">{group.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.permissions.map((perm) => (
                      <label
                        key={perm.key}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                          editFormData.permissions.includes(perm.key)
                            ? 'bg-vida-100 text-vida-700 border-vida-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } border`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={editFormData.permissions.includes(perm.key)}
                          onChange={() => togglePermission(perm.key, true)}
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowEditModal(false); setSelectedRole(null); }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar cambios
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar rol"
        message={`¿Estás seguro de eliminar el rol "${selectedRole?.name}"?`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

function ShieldCheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
    </svg>
  );
}
